import { create } from 'zustand';
import { CartItem, CustomerDetails } from '@/types';
import { requestNotificationPermission } from '@/lib/firebase';
import { getDomain } from '@/lib/utils';

interface StoreState {
  cart: CartItem[];
  addToCart: (product: CartItem) => void;
  removeFromCart: (id: number) => void;
  updateCartQuantity: (id: number, quantity: number, variations?: CartItem['selectedVariations']) => void;
  clearCart: () => void;
  wishlist: number[];
  toggleWishlist: (productId: number) => void;
  setWishlist: (productIds: number[]) => void;
  customerDetails: CustomerDetails;
  setCustomerDetails: (details: CustomerDetails) => void;
  notificationsEnabled: boolean;
  setNotificationsEnabled: (enabled: boolean) => void;
  subscribeToNotifications: () => Promise<void>;
}

export const useStore = create<StoreState>((set) => ({

  cart: JSON.parse(localStorage.getItem('cart') || '[]'),
  addToCart: (product) =>
    set((state) => {
      const existingItem = state.cart.find((item) => item.id === product.id);
      let newCart;

      if (existingItem) {
        if (product.selectedVariations && product.selectedVariations.length > 0) {
          // Create a map to track unique variations by variation name (more reliable than v_id)
          const variationMap = new Map<string, typeof product.selectedVariations[0]>();
          
          // Add existing variations to map using variation name as key
          existingItem.selectedVariations?.forEach(v => {
            if (v.variation) {
              variationMap.set(v.variation, v);
            }
          });
          
          // Merge or add new variations
          product.selectedVariations.forEach(newVar => {
            if (newVar.variation) {
              const existing = variationMap.get(newVar.variation);
              if (existing) {
                // Update quantity for existing variation
                variationMap.set(newVar.variation, {
                  ...existing,
                  quantity: existing.quantity + newVar.quantity
                });
              } else {
                // Add new variation
                variationMap.set(newVar.variation, newVar);
              }
            }
          });
          
          // Convert map back to array
          const mergedVariations = Array.from(variationMap.values());
          
          newCart = state.cart.map((item) =>
            item.id === product.id
              ? { ...item, selectedVariations: mergedVariations }
              : item
          );
        } else {
          newCart = state.cart.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + product.quantity }
              : item
          );
        }
      } else {
        newCart = [...state.cart, product];
      }

      localStorage.setItem('cart', JSON.stringify(newCart));
      return { cart: newCart };
    }),

  removeFromCart: (id) =>
    set((state) => {
      const newCart = state.cart.filter((item) => item.id !== id);
      localStorage.setItem('cart', JSON.stringify(newCart));
      return { cart: newCart };
    }),

  updateCartQuantity: (id, quantity, variations) =>
    set((state) => {
      const newCart = state.cart.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity,
              selectedVariations: variations || item.selectedVariations,
            }
          : item
      );
      localStorage.setItem('cart', JSON.stringify(newCart));
      return { cart: newCart };
    }),

  clearCart: () =>
    set(() => {
      localStorage.removeItem('cart');
      // Also clear Stripe-related cart storage
      localStorage.removeItem('stripeCartItems');
      localStorage.removeItem('pendingStripeOrder');
      return { cart: [] };
    }),

  wishlist: JSON.parse(localStorage.getItem('wishlist') || '[]'),
  toggleWishlist: (productId) =>
    set((state) => {
      const newWishlist = state.wishlist.includes(productId)
        ? state.wishlist.filter((id) => id !== productId)
        : [...state.wishlist, productId];
      localStorage.setItem('wishlist', JSON.stringify(newWishlist));
      return { wishlist: newWishlist };
    }),

  setWishlist: (productIds) =>
    set(() => {
      localStorage.setItem('wishlist', JSON.stringify(productIds));
      return { wishlist: productIds };
    }),

  customerDetails: JSON.parse(localStorage.getItem('customerDetails') || '{}'),
  setCustomerDetails: (details) =>
    set(() => {
      localStorage.setItem('customerDetails', JSON.stringify(details));
      return { customerDetails: details };
    }),

  notificationsEnabled: localStorage.getItem('notificationsEnabled') === 'true',
  setNotificationsEnabled: (enabled) =>
    set(() => {
      localStorage.setItem('notificationsEnabled', String(enabled));
      return { notificationsEnabled: enabled };
    }),

  subscribeToNotifications: async () => {
    try {
      const token = await requestNotificationPermission();
      
      if (token) {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/subscribe-notifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, domain }),
        });

        if (!response.ok) {
          throw new Error('Failed to register notification token with server');
        }

        const data = await response.json();
        if (!data.status) {
          throw new Error(data.message || 'Failed to subscribe to notifications');
        }

        set({ notificationsEnabled: true });
        localStorage.setItem('notificationsEnabled', 'true');
      }
    } catch (error) {
      console.warn('Failed to subscribe to notifications:', error);
      set({ notificationsEnabled: false });
      localStorage.setItem('notificationsEnabled', 'false');
      throw error;
    }
  },
}));


